import { useState, type FormEvent } from 'react';
import { submitFormToEndpoint } from '@/lib/formSubmit';
import { vendorEventOptions, vendorStyleOptions } from '@/mocks/site';

const ENDPOINT = 'https://readdy.ai/api/form/dbacmfm9if4sq287d0n0';

const inputClass =
  'w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-[13px] text-foreground-900 placeholder:text-foreground-400 outline-none transition-all duration-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30';

const labelClass = 'flex items-center gap-2 text-[12px] font-bold text-foreground-800';

const requiredMark = <span className="text-[10px] font-bold text-primary-600">必須</span>;
const optionalMark = <span className="text-[10px] font-medium text-foreground-400">任意</span>;

export default function VendorForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [detailLength, setDetailLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    setErrorMessage('');
    setStatus('submitting');

    const result = await submitFormToEndpoint(formEl, ENDPOINT, '出店申込');

    if (result.ok) {
      setStatus('success');
      formEl.reset();
      setDetailLength(0);
      return;
    }

    setErrorMessage(result.message);
    setStatus('idle');
  };

  if (status === 'success') {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-lg bg-background-50 px-6 py-14 text-center md:px-10 md:py-20">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-check-line text-2xl" aria-hidden="true" />
        </span>
        <h3 className="mt-6 font-heading text-[20px] font-black text-foreground-950">送信が完了しました</h3>
        <p className="mt-3 max-w-sm text-[12.5px] leading-[1.9] text-foreground-600">
          出店のお申し込みありがとうございます。内容を確認のうえ、担当より順次ご連絡いたします。
        </p>
        <p className="mt-4 max-w-sm text-[11px] leading-relaxed text-foreground-400">
          ※ 自動返信メールは設定準備中です。お急ぎの場合は公式LINEまたはお電話でご連絡ください。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-7 cursor-pointer rounded-md border border-foreground-300 px-5 py-2.5 text-[12px] font-bold text-foreground-700 transition-colors duration-300 hover:bg-background-100"
        >
          フォームに戻る
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 p-5 md:p-8"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-heading text-[17px] font-black text-foreground-950 md:text-[19px]">出店申込フォーム</h3>
        <p className="text-[10px] en-label text-foreground-400">ENTRY</p>
      </div>
      <p className="mt-2 text-[11.5px] leading-relaxed text-foreground-500">
        <span className="text-primary-600">*</span> は必須項目です。内容を確認のうえ送信してください。
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-2 md:gap-5">
        <div>
          <label className={labelClass} htmlFor="vendor_name">
            出店者名 {requiredMark}
          </label>
          <input id="vendor_name" name="vendor_name" required className={`${inputClass} mt-2`} placeholder="山田 花子" />
        </div>

        <div>
          <label className={labelClass} htmlFor="vendor_company">
            会社名・屋号 {requiredMark}
          </label>
          <input
            id="vendor_company"
            name="vendor_company"
            required
            className={`${inputClass} mt-2`}
            placeholder="炭火食堂やまだ"
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="vendor_contact">
            担当者名 {optionalMark}
          </label>
          <input id="vendor_contact" name="vendor_contact" className={`${inputClass} mt-2`} placeholder="山田 花子" />
        </div>

        <div>
          <label className={labelClass} htmlFor="vendor_phone">
            電話番号 {requiredMark}
          </label>
          <input
            id="vendor_phone"
            name="vendor_phone"
            type="tel"
            required
            inputMode="tel"
            className={`${inputClass} mt-2`}
            placeholder="090-0000-0000"
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="vendor_email">
            メールアドレス {requiredMark}
          </label>
          <input
            id="vendor_email"
            name="email"
            type="email"
            required
            className={`${inputClass} mt-2`}
            placeholder="example@mail.com"
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="vendor_line">
            LINE ID {requiredMark}
          </label>
          <input id="vendor_line" name="vendor_line" required className={`${inputClass} mt-2`} placeholder="line_id" />
          <p className="mt-1 text-[10.5px] leading-relaxed text-foreground-400">
            ご連絡用に、LINE IDのご記入をお願いします。
          </p>
        </div>

        <div>
          <label className={labelClass} htmlFor="vendor_event">
            出店希望イベント {requiredMark}
          </label>
          <select id="vendor_event" name="vendor_event" required defaultValue="" className={`${inputClass} mt-2 cursor-pointer`}>
            <option value="" disabled>
              選択してください
            </option>
            {vendorEventOptions.map((option) => (
              <option key={option.value} value={option.label} disabled={!option.open}>
                {option.label}
                {option.open ? '' : '（受付停止中）'}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="vendor_date">
            希望日程 {optionalMark}
          </label>
          <input id="vendor_date" name="vendor_date" className={`${inputClass} mt-2`} placeholder="例：9月の土日希望" />
        </div>

        <div className="md:col-span-2">
          <label className={labelClass} htmlFor="vendor_details">
            出店内容・販売商品 {requiredMark}
          </label>
          <textarea
            id="vendor_details"
            name="vendor_details"
            required
            maxLength={500}
            rows={4}
            onChange={(event) => setDetailLength(event.target.value.length)}
            className={`${inputClass} mt-2 resize-none leading-relaxed`}
            placeholder="提供予定のメニュー、価格帯、必要スペースなどをご記入ください。"
          />
          <p className="mt-1 text-right text-[10.5px] text-foreground-400">{detailLength} / 500</p>
        </div>

        <div>
          <label className={labelClass} htmlFor="vendor_style">
            出店形態 {requiredMark}
          </label>
          <select id="vendor_style" name="vendor_style" required defaultValue="" className={`${inputClass} mt-2 cursor-pointer`}>
            <option value="" disabled>
              選択してください
            </option>
            {vendorStyleOptions.map((style) => (
              <option key={style} value={style}>
                {style}
              </option>
            ))}
          </select>
        </div>

        <fieldset>
          <legend className={labelClass}>電源使用 {requiredMark}</legend>
          <div className="mt-2 flex gap-2">
            {['必要', '不要'].map((value) => (
              <label
                key={value}
                className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-3 text-[12.5px] text-foreground-700 transition-colors duration-300 has-[:checked]:border-primary-400 has-[:checked]:bg-primary-50 has-[:checked]:text-primary-800"
              >
                <input type="radio" name="vendor_power" value={value} defaultChecked={value === '不要'} className="accent-primary-500" />
                {value}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="md:col-span-2 rounded-md border border-background-200 bg-background-100 px-4 py-3">
          <p className="text-[11px] leading-[1.85] text-foreground-600">
            <i className="ri-error-warning-line mr-1 text-primary-600" aria-hidden="true" />
            ※テント・机・椅子・夜間の電気などのご準備は、全て出店者様でのご用意となります。
          </p>
        </div>

        <div className="md:col-span-2">
          <label className={labelClass} htmlFor="vendor_note">
            その他、運営への連絡事項 {optionalMark}
          </label>
          <textarea
            id="vendor_note"
            name="vendor_note"
            maxLength={500}
            rows={3}
            className={`${inputClass} mt-2 resize-none leading-relaxed`}
            placeholder="ご質問やご要望があればご記入ください。"
          />
        </div>
      </div>

      <div className="relative mt-5 overflow-hidden">
        <label className="hp-field" aria-hidden="true">
          company_alt
          <input name="company_alt" type="text" tabIndex={-1} autoComplete="off" readOnly className="hp-field" />
        </label>
      </div>

      {errorMessage && (
        <div role="alert" className="mt-5 rounded-md border border-primary-300 bg-primary-50 px-4 py-3">
          <p className="text-[12px] leading-relaxed text-primary-800">{errorMessage}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-4 text-[13px] font-bold text-background-50 transition-colors duration-300 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
            送信中...
          </>
        ) : (
          <>
            この内容で申し込む
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </>
        )}
      </button>

      <p className="mt-3 text-[10.5px] leading-relaxed text-foreground-400">
        送信いただいた内容は、本イベントの運営目的にのみ使用します。
      </p>
    </form>
  );
}